// A main pane and a side panel with a divider between them (byj-1wv.18),
// after brainyard-playground-apps' ResizablePanels: the divider is dragged,
// or moved with the arrow keys as a separator; the side collapses to a rail
// with its title turned on its side; the width and whether it is collapsed
// are kept in localStorage under `storageKey`, per page. Below the page's
// stacking breakpoint the CSS stacks the two and hides the divider. The side
// is at the end (a share's panel, the workbench's agent) or at the start
// (the workbench's files), and its fold can be the page's to hold, when the
// page has a control of its own for it (the workbench head's panel toggles).
import { useRef, type ReactNode } from "react";
import { dragDivider } from "../drag";
import { useKept, useKeptFlag } from "../kept";
import { t } from "../i18n";

export const SideMin = 240;
export const SideMax = 560;
export const SideDefault = 352;
export const RailPx = 40;

/** How wide the side may be, and is at first. */
export type SideBounds = { min: number; max: number; initial: number };
const sideBounds: SideBounds = { min: SideMin, max: SideMax, initial: SideDefault };

export function SplitPane({
  storageKey,
  main,
  side,
  sideTitle,
  open = false,
  at = "end",
  bounds = sideBounds,
  collapsed: held,
  onCollapsed,
  keepSide = false,
  className,
}: {
  storageKey: string;
  main: ReactNode;
  side: ReactNode;
  sideTitle: string;
  /**
   * The side shown whatever was kept, with no toggle: a page that shows it
   * as a pane of its own (the workbench's tabs on a narrow screen), where a
   * rail would leave that pane empty. What was kept is kept for later.
   */
  open?: boolean;
  /** which end the side is at */
  at?: "start" | "end";
  bounds?: SideBounds;
  /** the fold held by the page, which keeps it, and told of a change; else kept here */
  collapsed?: boolean;
  onCollapsed?: (collapsed: boolean) => void;
  /** the side drawn while folded, hidden, so what it holds (a tree unfolded, a prompt half written) outlives a fold */
  keepSide?: boolean;
  className?: string;
}) {
  const clamp = (w: number) => Math.min(bounds.max, Math.max(bounds.min, w));
  // read once: a page's split keeps its key
  const [width, keepWidth] = useKept(
    `${storageKey}.width`,
    bounds.initial,
    (s) => {
      const w = Number(s);
      return Number.isFinite(w) && w >= bounds.min && w <= bounds.max ? w : undefined;
    },
    (w) => String(Math.round(w)),
  );
  const [keptCollapsed, keepCollapsed] = useKeptFlag(`${storageKey}.collapsed`, false);
  const collapsed = (held ?? keptCollapsed) && !open;
  const fold = (c: boolean) => (onCollapsed ? onCollapsed(c) : keepCollapsed(c));
  const box = useRef<HTMLDivElement>(null);
  const set = (w: number, c: boolean) => {
    keepWidth(clamp(w));
    fold(c);
  };
  // the arrow toward the side widens it
  const start = at === "start";

  // kept once, as it is let go: not on every move
  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (collapsed) return;
    e.preventDefault();
    const el = box.current;
    if (!el) return;
    const { left, right } = el.getBoundingClientRect();
    let w = width;
    dragDivider(
      "resizing",
      (ev) => {
        w = clamp(start ? ev.clientX - left : right - ev.clientX);
        keepWidth(w, false);
      },
      () => keepWidth(w),
    );
  };
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const step = e.shiftKey ? 48 : 16;
    switch (e.key) {
      case "ArrowLeft":
        set(width + (start ? -step : step), false);
        break;
      case "ArrowRight":
        set(width + (start ? step : -step), false);
        break;
      case "Home":
        set(bounds.max, false);
        break;
      case "End":
        set(bounds.min, false);
        break;
      case "Enter":
      case " ":
        set(width, !collapsed);
        break;
      default:
        return;
    }
    e.preventDefault();
  };

  const mainPane = <div className="split-main">{main}</div>;
  const divider = (
    <div
      className="split-divider"
      role="separator"
      aria-orientation="vertical"
      aria-label={t("session:sideResize")}
      aria-valuenow={collapsed ? 0 : Math.round(width)}
      aria-valuemin={bounds.min}
      aria-valuemax={bounds.max}
      tabIndex={0}
      onPointerDown={onPointerDown}
      onKeyDown={onKeyDown}
      onDoubleClick={() => set(bounds.initial, false)}
    />
  );
  // the toggle points the way the side goes: away while open, back while folded
  const pointsRight = start === collapsed;
  const chevron = (
    <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">
      <path d={pointsRight ? "M6 3.5 10.5 8 6 12.5" : "M10 3.5 5.5 8 10 12.5"} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
  const sidePane = (
    <aside className="split-side" style={collapsed ? undefined : { width }} aria-label={sideTitle} data-testid="split-side">
      {!open && (
        <button
          type="button"
          className="split-toggle"
          onClick={() => set(width, !collapsed)}
          aria-expanded={!collapsed}
          title={collapsed ? t("session:sideExpand") : t("session:sideCollapse")}
        >
          {chevron}
        </button>
      )}
      {collapsed && (
        <button type="button" className="split-rail-title" onClick={() => set(width, false)}>
          {sideTitle}
        </button>
      )}
      {(!collapsed || keepSide) && (
        <div className="split-side-body" hidden={collapsed}>
          {side}
        </div>
      )}
    </aside>
  );

  return (
    <div className={`split split-${at}${collapsed ? " split-collapsed" : ""}${className ? ` ${className}` : ""}`} ref={box}>
      {start ? (
        <>
          {sidePane}
          {divider}
          {mainPane}
        </>
      ) : (
        <>
          {mainPane}
          {divider}
          {sidePane}
        </>
      )}
    </div>
  );
}
